// ABOUTME: Detail page for a specific country configuration entity.
// ABOUTME: Renders ScopeNavigator and content panel; entity name/description come from the entity-catalog API.
import {useQueryClient} from '@tanstack/react-query'
import {Content} from '@hconnect/uikit'
import {Button, Icon, Modal, ModalFooter, ModalTitle, TextSemiBold, TextSmallRegular} from '@hm/fundamental'
import {mdiCancel, mdiClose, mdiEarth} from '@mdi/js'
import {useSnackbar} from 'notistack'
import type {SnackbarKey} from 'notistack'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'

import {useTranslation} from '../../../hooks/useTranslation'
import {useSearchScopes} from './hooks/useScopeSearch'
import {fromEntitySlug, isConflictError, SCOPE_LEVEL_OPTIONS, useCountryName, useSelectedScope} from './utils'
import {EntityContentFooter} from './components/EntityContentFooter'
import {EntityContentHeader} from './components/EntityContentHeader'
import {ConfigImportExportActions} from './components/ConfigImportExportActions'
import {EntityContentPanel} from './components/EntityContentPanel'
import {DetailLayout} from './components/EntityLayout'
import {ScopeNavigator, ScopeNavigatorVariant} from './components/ScopeNavigator'
import {ENTITY_FORM_REGISTRY} from './components/entityFormRegistry'
import {useNavigatorScopes} from './hooks/useNavigatorScopes'
import {useSlotManagementEnabled} from './hooks/useSlotManagementEnabled'
import {useEntity, useEntityCatalogGlobal} from './hooks/useEntitiesList'
import type {LeadTimeFormOptions} from './components/OrderIntakeLeadTimeForm'
import {EntityActionResult, EntityFormActions, EntityFormProvider, EntityFormContextValue} from './context/EntityFormContext'
import {
  HeaderValuesStatus,
  EntityType,
  ScopeItem,
  ScopeResultItem,
  QuantityEntityOptions,
  RetarderEntityOptions,
  TrucksEntityOptions,
  UnloadingMethodEntityOptions,
  OrderIntakeLeadTimeEntityOptions,
  LoadSpacingEntityOptions,
  OverbookingAllowanceEntityOptions,
  PumpSettingsEntityOptions,
} from './types'
import {CalculationMethod, CALCULATION_METHODS, ReferenceEvent, REFERENCE_EVENTS, TIME_UOM_OPTIONS} from './types/orderIntakeLeadTime'
import {filterToKnown, uomRefsToOptions} from './utils/entityCatalogOptions'
import {EntityContentError} from './components/EntityContentError'

export const CountryConfigurationsEntity = () => {
  const {t} = useTranslation()
  const {enqueueSnackbar, closeSnackbar} = useSnackbar()
  const history = useHistory()
  const {countryId, businessLine, entityType: entitySlug} = useParams<{
    countryId: string
    businessLine: string
    entityType: string
  }>()
  const entityId = fromEntitySlug(entitySlug) as EntityType

  const isSlotManagementEnabled = useSlotManagementEnabled(countryId, businessLine)
  const queryClient = useQueryClient()

  const businessLineLabel = businessLine?.toUpperCase() ?? ''
  const countryName = useCountryName(countryId)
  const {selectedScopeId, handleScopeSelect} = useSelectedScope()
  const {data: entity, isLoading: isEntitiesLoading} = useEntity(countryId, businessLine, entityId)
  const {data: catalogGlobal} = useEntityCatalogGlobal(countryId, businessLine)

  // Per-entity catalog options (backend = source of truth; forms fall back to hardcoded lists).
  const quantityOptions = entity?.options as QuantityEntityOptions | undefined
  const retarderOptions = entity?.options as RetarderEntityOptions | undefined
  const unloadingOptions = entity?.options as UnloadingMethodEntityOptions | undefined
  const overbookingAllowanceOptions = entity?.options as OverbookingAllowanceEntityOptions | undefined
  const pumpSettingsOptions = entity?.options as PumpSettingsEntityOptions | undefined
  const loadSpacingOptions = entity?.options as LoadSpacingEntityOptions | undefined
  const trucksOptions = entity?.options as TrucksEntityOptions | undefined

  const leadTimeFormOptions = useMemo<LeadTimeFormOptions | undefined>(() => {
    const leadTime = (entity?.options as OrderIntakeLeadTimeEntityOptions | undefined)?.leadTime
    if (!leadTime) return undefined
    return {
      availableFields: leadTime.availableFields,
      // Drop any backend codes that aren't known FE union members so unrecognized values never
      // reach the i18n label lookup; an empty/undefined result lets the form fall back to defaults.
      referenceEvents: filterToKnown<ReferenceEvent>(leadTime.availableReferenceEvents, REFERENCE_EVENTS),
      calculationMethods: filterToKnown<CalculationMethod>(
        leadTime.availableCalculationMethods,
        CALCULATION_METHODS,
      ),
      timeUomOptions: leadTime.availableUnitsOfMeasure && leadTime.availableUnitsOfMeasure.length > 0
        ? TIME_UOM_OPTIONS.filter(opt => leadTime.availableUnitsOfMeasure!.some(ref => ref.id === opt.value))
        : undefined,
    }
  }, [entity?.options])

  const leadTimeDistanceUomOptions = useMemo(() => {
    const refs = (entity?.options as OrderIntakeLeadTimeEntityOptions | undefined)?.farDistanceModifier
      ?.availableDistanceUnitsOfMeasure
    return refs && refs.length > 0 ? uomRefsToOptions(refs) : undefined
  }, [entity?.options])
  const scopeVariant: ScopeNavigatorVariant =
    entity?.applicableLevels.some((l) => l !== 'country') ? 'overriddenScopes' : 'countryDefault'
  const {data: navigatorScopes, rootScopeId} = useNavigatorScopes(
    entityId,
    countryId,
    businessLine
  )

  const [temporaryScope, setTemporaryScope] = useState<ScopeItem | null>(null)
  const [selectedTemporaryScopeId, setSelectedTemporaryScopeId] = useState<string | null>(null)
  const selectedTemporaryScope = temporaryScope && temporaryScope.id === selectedTemporaryScopeId ? temporaryScope : null
  const selectedExistingScopeId = selectedTemporaryScope ? undefined : selectedScopeId ?? undefined

  useEffect(() => {
    if (rootScopeId && !selectedScopeId) {
      handleScopeSelect(rootScopeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fires only when rootScopeId first resolves
  }, [rootScopeId])

  const isCountryDefault =
    !selectedTemporaryScope &&
    (!selectedScopeId ||
    (rootScopeId !== undefined && selectedScopeId === rootScopeId))

  const scopeTitle = useMemo(() => {
    if (isCountryDefault) return t('countryConfigurations.entity.header.countryDefault')
    if (selectedTemporaryScope) return selectedTemporaryScope.displayName
    const scope = navigatorScopes.find((s) => s.id === selectedScopeId)
    if (scope) return scope.displayName
    if (temporaryScope && temporaryScope.id === selectedScopeId) return temporaryScope.displayName
    return selectedScopeId ?? ''
  }, [navigatorScopes, isCountryDefault, selectedScopeId, selectedTemporaryScope, temporaryScope, t])

  const valuesStatus: HeaderValuesStatus | undefined = useMemo(() => {
    if (isCountryDefault) return undefined
    if (selectedTemporaryScope) return 'allInherited'
    const scope = navigatorScopes.find((s) => s.id === selectedScopeId)
    if (scope) return scope.isOverridden ? 'overridden' : 'allInherited'
    if (temporaryScope && temporaryScope.id === selectedScopeId) return 'allInherited'
    return undefined
  }, [navigatorScopes, isCountryDefault, selectedScopeId, selectedTemporaryScope, temporaryScope])

  const scopeIconInfo = useMemo(() => {
    if (isCountryDefault) return {icon: mdiEarth, iconClassName: 'text-foreground_faded'}
    const scope = navigatorScopes.find((s) => s.id === selectedScopeId)
    const level = scope?.level ?? selectedTemporaryScope?.level ?? (temporaryScope && temporaryScope.id === selectedScopeId ? temporaryScope.level : undefined)
    if (!level) return undefined
    const levelOption = SCOPE_LEVEL_OPTIONS.find((o) => o.value === level)
    return levelOption ? {icon: levelOption.icon, iconClassName: levelOption.iconClassName} : undefined
  }, [navigatorScopes, isCountryDefault, selectedScopeId, selectedTemporaryScope, temporaryScope])

  const FormComponent = ENTITY_FORM_REGISTRY[entityId]

  const [registeredActions, setRegisteredActions] = useState<EntityFormActions | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const registerActions = useCallback((actions: EntityFormActions) => {
    setRegisteredActions(actions)
  }, [])

  const handleSavingChange = useCallback((saving: boolean) => {
    setIsSaving(saving)
  }, [])

  // Clear any previously registered actions / saving state when the current entity has no form
  // (unsupported entity type), so the footer can't trigger a stale entity's save/reset.
  useEffect(() => {
    if (!FormComponent) {
      setRegisteredActions(null)
      setIsSaving(false)
    }
  }, [FormComponent])

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isFormValid, setIsFormValid] = useState(true)
  const [formResetKey, setFormResetKey] = useState(0)
  const [leaveModalOpen, setLeaveModalOpen] = useState(false)
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [conflictModalOpen, setConflictModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  useEffect(() => {
    setHasUnsavedChanges(false)
    setIsFormValid(true)
  }, [entityId, selectedScopeId, selectedTemporaryScopeId])

  useEffect(() => {
    setTemporaryScope(null)
    setSelectedTemporaryScopeId(null)
  }, [entityId])

  const guardUnsavedChanges = useCallback((action: () => void) => {
    if (hasUnsavedChanges) {
      setPendingAction(() => action)
      setLeaveModalOpen(true)
    } else {
      action()
    }
  }, [hasUnsavedChanges])

  const handleBack = useCallback(() => {
    guardUnsavedChanges(() => history.push(`/country-configurations/${countryId}/${businessLine}`))
  }, [guardUnsavedChanges, history, countryId, businessLine])

  const {search: searchScopes} = useSearchScopes(entityId, countryId, businessLine)

  const guardedScopeSelect = useCallback((id: string) => {
    guardUnsavedChanges(() => {
      setSelectedTemporaryScopeId(null)
      handleScopeSelect(id)
    })
  }, [guardUnsavedChanges, handleScopeSelect])

  const guardedTemporaryScopeSelect = useCallback((id: string) => {
    guardUnsavedChanges(() => setSelectedTemporaryScopeId(id))
  }, [guardUnsavedChanges])

  const handleOpenScope = useCallback((scope: ScopeResultItem) => {
    const scopeId = scope.id
    if (!scopeId && (!scope.parentId || !Object.keys(scope.scopeData ?? {}).length)) {
      enqueueSnackbar(t('countryConfigurations.entity.scopeCannotBeIdentified'), {
        variant: 'error',
        anchorOrigin: {vertical: 'top', horizontal: 'center'},
      })
      return
    }
    const temporaryScopeId = scopeId ?? `pending-${scope.level}-${scope.displayNumber || scope.displayName}`
    const isAlreadyInList = navigatorScopes.some(s => s.id === scopeId)
    if (scopeId && isAlreadyInList) {
      guardedScopeSelect(scopeId)
      return
    }
    guardUnsavedChanges(() => {
      setTemporaryScope({
        id: temporaryScopeId,
        parentId: scope.parentId,
        displayName: scope.displayName,
        displayNumber: scope.displayNumber,
        level: scope.level,
        scopeData: scope.scopeData,
        isOverridden: scope.isOverridden
      })
      if (scopeId) {
        setSelectedTemporaryScopeId(null)
        handleScopeSelect(scopeId)
      } else {
        setSelectedTemporaryScopeId(temporaryScopeId)
      }
    })
  }, [navigatorScopes, guardedScopeSelect, guardUnsavedChanges, handleScopeSelect, enqueueSnackbar, t])

  const pendingScope = useMemo(() => {
    if (!selectedTemporaryScope?.level || !selectedTemporaryScope.parentId || !selectedTemporaryScope.scopeData) return undefined
    return {
      countryId,
      businessLine: businessLine.toUpperCase(),
      level: selectedTemporaryScope.level,
      parentId: selectedTemporaryScope.parentId,
      identifiers: selectedTemporaryScope.scopeData,
    }
  }, [businessLine, countryId, selectedTemporaryScope])

  const handleConfirmLeave = useCallback(() => {
    setLeaveModalOpen(false)
    setFormResetKey((k) => k + 1)
    pendingAction?.()
    setPendingAction(null)
  }, [pendingAction])

  const handleDirtyChange = useCallback((dirty: boolean) => {
    setHasUnsavedChanges(dirty)
  }, [])

  const handleValidChange = useCallback((valid: boolean) => {
    setIsFormValid(valid)
  }, [])

  const entityFormValue = useMemo<EntityFormContextValue>(() => ({
    countryId,
    businessLine,
    selectedScopeId: selectedExistingScopeId,
    pendingScope,
    rootScopeId,
    isCountryDefault,
    isReadOnly: false,
    isSlotManagementEnabled,
    formResetKey,
    quantityOptions,
    retarderOptions,
    unloadingOptions,
    leadTimeFormOptions,
    leadTimeDistanceUomOptions,
    dayCodes: catalogGlobal?.dayCodes,
    overbookingAllowanceOptions,
    pumpSettingsOptions,
    loadSpacingOptions,
    trucksOptions,
    onDirtyChange: handleDirtyChange,
    onValidChange: handleValidChange,
    onSavingChange: handleSavingChange,
    registerActions,
  }), [
    countryId,
    businessLine,
    selectedExistingScopeId,
    pendingScope,
    rootScopeId,
    isCountryDefault,
    isSlotManagementEnabled,
    formResetKey,
    quantityOptions,
    retarderOptions,
    unloadingOptions,
    leadTimeFormOptions,
    leadTimeDistanceUomOptions,
    catalogGlobal?.dayCodes,
    overbookingAllowanceOptions,
    pumpSettingsOptions,
    loadSpacingOptions,
    trucksOptions,
    handleDirtyChange,
    handleValidChange,
    handleSavingChange,
    registerActions,
  ])

  const executeEntityAction = useCallback((
    action: ((onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void) | undefined,
    getSuccessMessage: (result?: EntityActionResult) => string | React.ReactNode,
    errorKey: string,
    extraOptions?: {action?: (key: SnackbarKey) => React.ReactNode; snackbarKey?: SnackbarKey},
  ) => {
    if (!action) {
      console.warn(`Unhandled entity type: ${entityId}`)
      return
    }
    action(
      (result) => {
        setHasUnsavedChanges(false)
        enqueueSnackbar(getSuccessMessage(result), {
          variant: 'success',
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
          ...(extraOptions?.action ? {action: extraOptions.action} : {}),
          ...(extraOptions?.snackbarKey ? {key: extraOptions.snackbarKey, persist: true} : {}),
        })
        if (result?.createdScopeId && selectedTemporaryScope) {
          setTemporaryScope(null)
          setSelectedTemporaryScopeId(null)
          handleScopeSelect(result.createdScopeId)
        } else if (result?.wasDelete && rootScopeId && selectedExistingScopeId && selectedExistingScopeId !== rootScopeId) {
          handleScopeSelect(rootScopeId)
        }
      },
      (error) => {
        if (isConflictError(error)) {
          queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey.includes(countryId) &&
              query.queryKey.includes(businessLine),
          })
          setConflictModalOpen(true)
          setFormResetKey((k) => k + 1)
          setHasUnsavedChanges(false)
        } else {
          enqueueSnackbar(t(errorKey), {
            variant: 'error',
            anchorOrigin: {vertical: 'top', horizontal: 'center'},
          })
        }
      }
    )
  }, [queryClient, countryId, businessLine, entityId, enqueueSnackbar, closeSnackbar, t, rootScopeId, selectedExistingScopeId, handleScopeSelect])

  const handleSave = useCallback(() => {
    const isOverriddenScopeSave = Boolean(pendingScope || (selectedExistingScopeId && selectedExistingScopeId !== rootScopeId))
    const snackbarKey = 'country-config-scope-save'

    const getSuccessMessage = (result?: EntityActionResult): React.ReactNode => {
      if (isOverriddenScopeSave) {
        const descriptionKey = result?.wasDelete
          ? 'countryConfigurations.entity.changesSavedScopeDeescalated'
          : 'countryConfigurations.entity.changesSavedScopeOverridden'
        return (
          <div className="flex items-start justify-between gap-2 w-full">
            <div className="flex flex-col">
              <TextSemiBold>{t('countryConfigurations.entity.changesSaved')}</TextSemiBold>
              <TextSmallRegular>{t(descriptionKey)}</TextSmallRegular>
            </div>
            <Button
              variant="tertiary"
              type="button"
              icon={mdiClose}
              size="small"
              aria-label={t('countryConfigurations.entity.closeSnackbarAriaLabel')}
              onClick={() => closeSnackbar(snackbarKey)}
              className="text-white hover:!bg-white/20 active:!bg-white/30 shrink-0"
            />
          </div>
        )
      }
      return t('countryConfigurations.entity.changesSaved')
    }

    executeEntityAction(
      registeredActions?.handleSave,
      getSuccessMessage,
      'countryConfigurations.entity.saveFailed',
      isOverriddenScopeSave ? {snackbarKey} : undefined,
    )
  }, [executeEntityAction, registeredActions, selectedExistingScopeId, rootScopeId, pendingScope, t, closeSnackbar])

  const handleDiscard = () => {
    setFormResetKey((k) => k + 1)
  }

  const handleReset = useCallback(() => {
    setResetModalOpen(true)
  }, [])

  const handleConfirmReset = useCallback(() => {
    setResetModalOpen(false)
    executeEntityAction(
      registeredActions?.handleReset,
      () => t('countryConfigurations.entity.changesReset'),
      'countryConfigurations.entity.resetFailed',
    )
  }, [executeEntityAction, registeredActions, t])

  return (
    <Content>
      <DetailLayout
        scopeNavigator={
          entity && (
            <ScopeNavigator
              variant={scopeVariant}
              selectedScopeId={selectedTemporaryScopeId ?? selectedScopeId ?? undefined}
              onScopeSelect={guardedScopeSelect}
              onTemporaryScopeSelect={guardedTemporaryScopeSelect}
              overriddenScopes={navigatorScopes}
              rootScopeId={rootScopeId}
              temporaryScope={temporaryScope}
              onOpenScope={handleOpenScope}
              onVerify={searchScopes}
              backLabel={t('countryConfigurations.entity.scopeNavigator.backLabel', {
                businessLine: businessLineLabel,
                countryName
              })}
              onBack={handleBack}
              entityTitle={entity?.name ?? ''}
              entityTooltip={entity?.description ?? ''}
            />
          )
        }
      >
        <EntityContentPanel
          header={
            <EntityContentHeader
              scopeTitle={scopeTitle}
              parent={
                !isCountryDefault
                  ? {
                      label: t('countryConfigurations.entity.header.countryDefault'),
                      onClick: () => rootScopeId && guardedScopeSelect(rootScopeId)
                    }
                  : undefined
              }
              valuesStatus={valuesStatus}
              onReset={valuesStatus === 'overridden' ? handleReset : undefined}
              isLoading={isEntitiesLoading}
              scopeIcon={scopeIconInfo?.icon}
              scopeIconClassName={scopeIconInfo?.iconClassName}
              actions={
                <ConfigImportExportActions
                  entityType={entityId}
                  entityName={entity?.name ?? ''}
                  countryId={countryId}
                  businessLine={businessLine}
                />
              }
            />
          }
          footer={
            <EntityContentFooter
              onSave={handleSave}
              onDiscard={handleDiscard}
              hasUnsavedChanges={hasUnsavedChanges}
              hasErrors={hasUnsavedChanges && !isFormValid}
              isLoading={isEntitiesLoading || !entity}
              isSaving={isSaving}
            />
          }
        >
          {!entity && !isEntitiesLoading ? (
            <EntityContentError />
          ) : FormComponent ? (
            <EntityFormProvider value={entityFormValue}>
              <FormComponent />
            </EntityFormProvider>
          ) : null}
        </EntityContentPanel>
      </DetailLayout>

      <Modal open={resetModalOpen} onClose={() => setResetModalOpen(false)}>
        <ModalTitle>{t('countryConfigurations.entity.resetModal.title')}</ModalTitle>
        <p className="pt-4 text-base text-foreground leading-6">
          {t('countryConfigurations.entity.resetModal.body1')}
        </p>
        <p className="pt-2 text-base text-foreground leading-6">
          {t('countryConfigurations.entity.resetModal.body2')}
        </p>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setResetModalOpen(false)}>
            {t('countryConfigurations.entity.resetModal.cancel')}
          </Button>
          <Button variant="negative" onClick={handleConfirmReset}>
            {t('countryConfigurations.entity.resetModal.confirm')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal open={conflictModalOpen} onClose={() => setConflictModalOpen(false)}>
        <ModalTitle>
          <span className="flex items-center gap-2">
            <Icon path={mdiCancel} size="regular" />
            {t('countryConfigurations.entity.conflictModal.title')}
          </span>
        </ModalTitle>
        <p className="pt-4 text-base text-foreground leading-6">
          {t('countryConfigurations.entity.conflictModal.body')}
        </p>
        <ModalFooter>
          <Button variant="secondary" className="w-full" onClick={() => setConflictModalOpen(false)}>
            {t('countryConfigurations.entity.conflictModal.confirm')}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal open={leaveModalOpen} onClose={() => { setLeaveModalOpen(false); setPendingAction(null) }}>
        <ModalTitle>{t('countryConfigurations.entity.leaveModal.title')}</ModalTitle>
        <p className="pt-4 text-base text-foreground leading-6">
          {t('countryConfigurations.entity.leaveModal.body')}
        </p>
        <ModalFooter>
          <Button variant="secondary" onClick={() => { setLeaveModalOpen(false); setPendingAction(null) }}>
            {t('countryConfigurations.entity.leaveModal.backToEditing')}
          </Button>
          <Button variant="negative" onClick={handleConfirmLeave}>
            {t('countryConfigurations.entity.leaveModal.discardChanges')}
          </Button>
        </ModalFooter>
      </Modal>
    </Content>
  )
}